// Epic particle effect system for discoveries and celebrations
import React, { useEffect, useRef } from 'react';
import { particles as particleConfig } from '../../theme/designTokens';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
  rotation: number;
  rotationSpeed: number;
}

interface ParticleSystemProps {
  trigger: boolean;
  type?: 'discovery' | 'combine' | 'celebration';
  onComplete?: () => void;
  centerX?: number;
  centerY?: number;
}

const ParticleSystem: React.FC<ParticleSystemProps> = ({
  trigger,
  type = 'discovery',
  onComplete,
  centerX,
  centerY,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!trigger) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Get particle config for type
    const config = particleConfig[type];

    // Create particles
    const cx = centerX ?? canvas.width / 2;
    const cy = centerY ?? canvas.height / 2;

    particlesRef.current = Array.from({ length: config.count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = config.speed.min + Math.random() * (config.speed.max - config.speed.min);

      return {
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2, // Add upward bias
        size: config.size.min + Math.random() * (config.size.max - config.size.min),
        color: config.colors[Math.floor(Math.random() * config.colors.length)],
        life: config.lifetime,
        maxLife: config.lifetime,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
      };
    });

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let aliveCount = 0;

      particlesRef.current.forEach((particle) => {
        if (particle.life <= 0) return;

        aliveCount++;

        // Update physics
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.15; // Gravity
        particle.life -= 16; // Decrease life (assuming ~60fps)
        particle.rotation += particle.rotationSpeed;

        // Calculate alpha based on life
        const alpha = particle.life / particle.maxLife;

        // Draw particle
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(particle.x, particle.y);
        ctx.rotate(particle.rotation);

        // Draw as a star/sparkle
        ctx.fillStyle = particle.color;
        ctx.beginPath();

        // Draw a star shape
        for (let i = 0; i < 5; i++) {
          const angle = (i * 4 * Math.PI) / 5;
          const radius = i % 2 === 0 ? particle.size : particle.size / 2;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.closePath();
        ctx.fill();

        // Add glow
        ctx.shadowBlur = 10;
        ctx.shadowColor = particle.color;
        ctx.fill();

        ctx.restore();
      });

      if (aliveCount > 0) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        onComplete?.();
      }
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [trigger, type, onComplete, centerX, centerY]);

  if (!trigger) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
};

export default ParticleSystem;
